import { useState } from 'react';
import {
  Badge,
  Button,
  Switch,
  TabList,
  Tab,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  OpenRegular,
  AddRegular,
  People20Regular,
  Info20Regular,
  Briefcase20Regular,
  Edit20Regular,
} from '@fluentui/react-icons';
import {
  useAdminUsers,
  useAdminMissingGroups,
  useSetUserRole,
  useAdminTrash,
  useAdminRestore,
  useAdminSyncRuns,
  useBackfillAttachmentNames,
} from '@/hooks/useAdmin';
import { ArrowResetRegular } from '@fluentui/react-icons';
import { FolderItemIcon, MailItemIcon } from '@/components/ItemIcon';
import { useContainers } from '@/hooks/useContainers';
import { useUiStore } from '@/stores/uiStore';
import { useWhoami } from '@/hooks/useWhoami';
import { ProjectGroupDialog } from './ProjectGroupDialog';
import { RenameSpaceDialog } from './RenameSpaceDialog';
import { SpacesOverviewPanel } from './SpacesOverviewPanel';
import type { MissingGroupProject, SyncRunStatus, SyncSource } from '@/services/api/endpoints';
import type { Container } from '@/types/domain';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
  },
  // `overflowX: auto` sur le CONTENEUR (et non sur la TabList) : la TabList doit
  // garder sa largeur naturelle pour que ses onglets ne se compriment pas.
  tabStrip: { overflowX: 'auto', maxWidth: '100%' },
  tabContent: {
    paddingTop: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  empty: {
    padding: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  prismLink: { textDecoration: 'none' },
  toolbar: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  hintBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  hintBannerIcon: { flexShrink: 0, marginTop: '2px', color: tokens.colorBrandForeground1 },
  primaryBtn: { whiteSpace: 'nowrap' },
  spaceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  ellipsis: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  srcInfo: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  errorHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  numCell: { width: '60px', whiteSpace: 'nowrap' },
  statusCell: { width: '72px', whiteSpace: 'nowrap' },
  actionCell: { width: '128px', textAlign: 'right', paddingRight: 0 },
  iconActionCell: { width: '44px', textAlign: 'right', paddingRight: 0 },
  // Bloc de maintenance sous le tableau des synchros : séparé visuellement pour
  // qu'on ne le prenne pas pour une ligne du tableau.
  maintenance: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  actionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
});

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'subtle' | 'danger'> = {
  actif: 'success',
  standby: 'warning',
  termine: 'subtle',
  annule: 'danger',
};
const STATUS_LABEL: Record<string, string> = {
  actif: 'Actif',
  standby: 'Standby',
  termine: 'Terminé',
  annule: 'Annulé',
};

type SettingsTab = 'users' | 'sync' | 'overview' | 'spaces' | 'missing-groups' | 'trash';

export function SettingsView() {
  const styles = useStyles();
  const [tab, setTab] = useState<SettingsTab>('users');

  return (
    <div className={styles.root}>
      {/* Six onglets dans un panneau de ~350 px : la TabList de Fluent ne défile
          pas d'elle-même et les derniers onglets se retrouvaient hors du cadre,
          donc inatteignables. Le conteneur défile horizontalement. */}
      <div className={styles.tabStrip}>
        <TabList selectedValue={tab} onTabSelect={(_e, d) => setTab(d.value as SettingsTab)}>
          <Tab value="users">Utilisateurs</Tab>
          <Tab value="sync">Synchronisations</Tab>
          <Tab value="overview">Tous les espaces</Tab>
          <Tab value="spaces">Espaces de service</Tab>
          <Tab value="missing-groups">Groupes 365 manquants</Tab>
          <Tab value="trash">Corbeille</Tab>
        </TabList>
      </div>

      <div className={styles.tabContent}>
        {tab === 'users' && <UsersPanel />}
        {tab === 'sync' && <SyncPanel />}
        {tab === 'overview' && <SpacesOverviewPanel />}
        {tab === 'spaces' && <SpacesPanel />}
        {tab === 'missing-groups' && <MissingGroupsPanel />}
        {tab === 'trash' && <TrashPanel />}
      </div>
    </div>
  );
}

function UsersPanel() {
  const styles = useStyles();
  const { data: me } = useWhoami();
  const { data, isPending } = useAdminUsers();
  const setRole = useSetUserRole();

  if (isPending) {
    return (
      <Skeleton>
        <SkeletonItem size={24} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
      </Skeleton>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className={styles.empty}>
        Personne n'a encore utilisé l'add-in. Les utilisateurs apparaîtront ici
        après leur première connexion au panneau.
      </div>
    );
  }

  return (
    <Table size="small">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>Utilisateur</TableHeaderCell>
          <TableHeaderCell>Admin</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((u) => {
          const isMe = me?.oid === u.oid;
          return (
            <TableRow key={u.oid}>
              <TableCell>
                <Text size={300} className={styles.ellipsis} title={u.upn ?? u.oid}>
                  {u.upn ?? u.oid}
                </Text>
                {isMe && (
                  <Text className={styles.hint} as="span" style={{ marginLeft: 6 }}>
                    (vous)
                  </Text>
                )}
              </TableCell>
              <TableCell>
                <Switch
                  checked={u.isAdmin}
                  disabled={setRole.isPending || (isMe && u.isAdmin)}
                  onChange={(_e, d) =>
                    setRole.mutate({ oid: u.oid, isAdmin: d.checked })
                  }
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Les seules synchros récurrentes réelles de l'add-in (cf. investigation) : la
// sync PRISM et l'auto-liaison des groupes 365. Liste fixe et honnête — on
// n'affiche pas de sources fantômes (Lucca n'existe pas ; SharePoint n'est pas
// synchronisé, juste un lien vers l'espace documentaire).
const SYNC_SOURCES: { key: SyncSource; label: string; description: string }[] = [
  {
    key: 'prism',
    label: 'PRISM : projets',
    description: 'Projets PRISM → dossiers · automatique toutes les 10 min',
  },
  {
    key: 'autolink_groups',
    label: 'Groupes Microsoft 365',
    description: 'Auto-liaison des groupes 365 aux projets · 2×/jour',
  },
  {
    key: 'aad_members',
    label: 'Membres des groupes Azure AD',
    description: 'Appartenances des groupes utilisés dans les droits · toutes les 10 min',
  },
];

const SYNC_STATUS: Record<
  SyncRunStatus['status'],
  { color: 'success' | 'danger' | 'warning'; label: string }
> = {
  success: { color: 'success', label: 'OK' },
  error: { color: 'danger', label: 'Erreur' },
  running: { color: 'warning', label: 'En cours' },
};

function timeAgo(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.round(h / 24)} j`;
}

function SyncPanel() {
  const styles = useStyles();
  const { data, isPending } = useAdminSyncRuns();
  const backfill = useBackfillAttachmentNames();

  if (isPending) {
    return (
      <Skeleton>
        <SkeletonItem size={24} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
      </Skeleton>
    );
  }

  const bySource = new Map((data ?? []).map((r) => [r.source, r] as const));

  return (
    <>
      <div className={styles.hintBanner}>
        <Info20Regular className={styles.hintBannerIcon} />
        <span>
          État des synchronisations automatiques qui alimentent l'add-in. Elles
          tournent en arrière-plan ; cet écran montre la dernière exécution de
          chacune.
        </span>
      </div>
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Synchronisation</TableHeaderCell>
            <TableHeaderCell className={styles.statusCell}>Statut</TableHeaderCell>
            <TableHeaderCell>Dernière sync</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {SYNC_SOURCES.map((src) => {
            const run = bySource.get(src.key);
            const st = run ? SYNC_STATUS[run.status] : undefined;
            const ts = run?.finishedAt ?? run?.startedAt ?? null;
            const ok = run?.itemsOk;
            const skipped = run?.itemsSkipped;
            return (
              <TableRow key={src.key}>
                <TableCell>
                  <div className={styles.spaceRow}>
                    {src.key === 'prism' ? <Briefcase20Regular /> : <People20Regular />}
                    <div className={styles.srcInfo}>
                      <Text size={300} className={styles.ellipsis} title={src.label}>
                        {src.label}
                      </Text>
                      <Text className={styles.hint} as="span">
                        {src.description}
                      </Text>
                    </div>
                  </div>
                </TableCell>
                <TableCell className={styles.statusCell}>
                  {st ? (
                    <Badge appearance="filled" color={st.color} size="small">
                      {st.label}
                    </Badge>
                  ) : (
                    <Badge appearance="tint" color="informative" size="small">
                      Jamais
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {ts ? (
                    <div className={styles.srcInfo}>
                      <Text size={300} title={new Date(ts).toLocaleString('fr-FR')}>
                        {timeAgo(ts)}
                      </Text>
                      {run?.status === 'error' && run.error ? (
                        <Text className={styles.errorHint} as="span" title={run.error}>
                          {run.error}
                        </Text>
                      ) : ok != null || skipped != null ? (
                        <Text className={styles.hint} as="span">
                          {ok ?? 0} ok
                          {skipped ? `, ${skipped} ignoré${skipped > 1 ? 's' : ''}` : ''}
                        </Text>
                      ) : null}
                    </div>
                  ) : (
                    <Text className={styles.hint}>-</Text>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Rattrapage ponctuel, distinct des synchros automatiques ci-dessus : il ne
          tourne que sur demande. Placé ici parce que c'est le même registre — de
          la donnée qu'on remet en phase — et qu'un onglet de plus pour un bouton
          serait disproportionné. */}
      <div className={styles.maintenance}>
        <Text size={300} weight="semibold">
          Rattrapage des pièces jointes
        </Text>
        <Text className={styles.hint} as="span">
          Renseigne les noms des pièces jointes des mails rangés avant que l'add-in ne sache les
          retenir. Ils apparaissent ensuite au survol du trombone dans la liste. Chaque passage
          traite un lot : à relancer jusqu'à ce qu'il ne reste rien.
        </Text>
        <div className={styles.actionRow}>
          <Button
            appearance="secondary"
            size="small"
            disabled={backfill.isPending}
            onClick={() => backfill.mutate(undefined)}
          >
            {backfill.isPending ? 'Rattrapage en cours…' : 'Lancer le rattrapage'}
          </Button>
          {backfill.data ? (
            <Text className={backfill.data.failed > 0 ? styles.errorHint : styles.hint} as="span">
              {backfill.data.updated} mis à jour
              {backfill.data.failed > 0 ? `, ${backfill.data.failed} en échec` : ''}
              {/* `remaining` peut être null si le comptage a échoué : ne pas
                  afficher « 0 restant » dans ce cas, ce serait affirmer la fin. */}
              {backfill.data.remaining === null
                ? ', reste inconnu'
                : `, ${backfill.data.remaining} restant${backfill.data.remaining > 1 ? 's' : ''}`}
              {backfill.data.done ? ' · terminé' : ''}
            </Text>
          ) : null}
          {backfill.isError ? (
            <Text className={styles.errorHint} as="span">
              {backfill.error.message}
            </Text>
          ) : null}
        </div>
      </div>
    </>
  );
}

function SpacesPanel() {
  const styles = useStyles();
  const setView = useUiStore((s) => s.setView);
  const { data: containers, isPending } = useContainers();
  const [renameTarget, setRenameTarget] = useState<Container | null>(null);

  const spaces = (containers ?? []).filter((c) => c.kind === 'team_space');

  return (
    <>
      <div className={styles.hintBanner}>
        <Info20Regular className={styles.hintBannerIcon} />
        <span>
          Un espace est rattaché à un groupe Microsoft 365 (existant ou créé à
          la volée). Réservé aux administrateurs de l'add-in.
        </span>
      </div>
      <div className={styles.toolbar}>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          className={styles.primaryBtn}
          onClick={() => setView('spaceCreate')}
        >
          Nouvel espace
        </Button>
      </div>

      {isPending ? (
        <Skeleton>
          <SkeletonItem size={24} />
          <SkeletonItem size={24} style={{ marginTop: 8 }} />
        </Skeleton>
      ) : spaces.length === 0 ? (
        <div className={styles.empty}>
          Aucun espace de service pour l'instant. Cliquez sur « Nouvel espace »
          pour en créer un.
        </div>
      ) : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Nom</TableHeaderCell>
              <TableHeaderCell>Groupe 365</TableHeaderCell>
              <TableHeaderCell className={styles.iconActionCell} aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {spaces.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <div className={styles.spaceRow}>
                    <People20Regular />
                    <Text size={300} className={styles.ellipsis} title={s.name}>
                      {s.name}
                    </Text>
                  </div>
                </TableCell>
                <TableCell>
                  <Text className={styles.hint} as="span">
                    {s.m365GroupId ? s.m365GroupId.slice(0, 8) + '…' : '-'}
                  </Text>
                </TableCell>
                <TableCell className={styles.iconActionCell}>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Edit20Regular />}
                    title={`Renommer « ${s.name} »`}
                    aria-label={`Renommer ${s.name}`}
                    onClick={() => setRenameTarget(s)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {renameTarget && (
        <RenameSpaceDialog
          space={renameTarget}
          open
          onOpenChange={(o) => {
            if (!o) setRenameTarget(null);
          }}
        />
      )}
    </>
  );
}

function TrashPanel() {
  const styles = useStyles();
  const { data, isPending } = useAdminTrash();
  const restore = useAdminRestore();

  if (isPending) {
    return (
      <Skeleton>
        <SkeletonItem size={24} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
      </Skeleton>
    );
  }
  const folders = data?.folders ?? [];
  const mails = data?.mails ?? [];
  const total = folders.length + mails.length;

  if (total === 0) {
    return (
      <div className={styles.empty}>
        La corbeille est vide. Les dossiers et mails supprimés y restent
        {data?.retentionDays ?? 30} jours avant suppression définitive.
      </div>
    );
  }

  const fmt = (iso: string) => new Date(iso).toLocaleString('fr-FR');

  return (
    <>
      <Text className={styles.hint}>
        Rétention {data?.retentionDays ?? 30} jours. Après ce délai les
        éléments sont supprimés définitivement par la tâche planifiée.
      </Text>
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Type</TableHeaderCell>
            <TableHeaderCell>Élément</TableHeaderCell>
            <TableHeaderCell>Supprimé le</TableHeaderCell>
            <TableHeaderCell aria-label="Restaurer" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {folders.map((f) => (
            <TableRow key={`folder-${f.id}`}>
              <TableCell>
                <FolderItemIcon />
              </TableCell>
              <TableCell>
                <Text size={300} className={styles.ellipsis} title={f.name}>
                  {f.name}
                </Text>
              </TableCell>
              <TableCell>{fmt(f.deleted_at)}</TableCell>
              <TableCell>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ArrowResetRegular />}
                  disabled={restore.isPending}
                  onClick={() => restore.mutate({ kind: 'folder', id: f.id })}
                >
                  Restaurer
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {mails.map((m) => (
            <TableRow key={`mail-${m.id}`}>
              <TableCell>
                <MailItemIcon />
              </TableCell>
              <TableCell>
                <Text size={300} className={styles.ellipsis} title={m.subject}>
                  {m.subject || '(sans objet)'}
                </Text>
              </TableCell>
              <TableCell>{fmt(m.deleted_at)}</TableCell>
              <TableCell>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ArrowResetRegular />}
                  disabled={restore.isPending}
                  onClick={() => restore.mutate({ kind: 'mail', id: m.id })}
                >
                  Restaurer
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function MissingGroupsPanel() {
  const styles = useStyles();
  const { data, isPending } = useAdminMissingGroups();
  const [dialogProject, setDialogProject] = useState<MissingGroupProject | null>(null);

  if (isPending) {
    return (
      <Skeleton>
        <SkeletonItem size={24} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
        <SkeletonItem size={24} style={{ marginTop: 8 }} />
      </Skeleton>
    );
  }
  if (!data || data.length === 0) {
    // « actifs » a disparu : depuis 0029 le rapport inclut les archivés.
    return (
      <div className={styles.empty}>Tous les projets ont un groupe Microsoft 365 associé.</div>
    );
  }

  const archivedCount = data.filter((p) => p.archived).length;

  return (
    <>
      <Text className={styles.hint}>
        {data.length} projet(s) sans groupe Microsoft 365
        {archivedCount > 0 && ` (dont ${archivedCount} archivé${archivedCount > 1 ? 's' : ''})`}.
        {/* Cette phrase était fausse jusqu'à 0029 : la doc et cet écran
            affirmaient que le rangement était désactivé, ce que le code ne
            faisait pas. Elle est vraie maintenant — et la conséquence est plus
            forte que « désactivé » : l'espace est introuvable. */}{' '}
        Ces espaces sont invisibles pour les utilisateurs — absents de l'arbre et
        de la recherche — et le rangement y est refusé tant qu'aucun groupe n'est
        associé.
      </Text>
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell className={styles.numCell}>N°</TableHeaderCell>
            <TableHeaderCell>Nom</TableHeaderCell>
            <TableHeaderCell className={styles.statusCell}>Statut</TableHeaderCell>
            <TableHeaderCell className={styles.actionCell} aria-label="Actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((p) => (
            <TableRow key={p.id}>
              <TableCell className={styles.numCell}>{p.number}</TableCell>
              <TableCell>
                <Text size={300} className={styles.ellipsis} title={p.name}>
                  {p.name}
                </Text>
              </TableCell>
              <TableCell className={styles.statusCell}>
                {/* Archivé PLUTÔT QUE le statut PRISM : un projet archivé sans
                    groupe est un écart de priorité moindre, et c'est cette
                    information-là qui décide s'il faut agir tout de suite. */}
                {p.archived ? (
                  <Badge appearance="outline" color="subtle" size="small">
                    Archivé
                  </Badge>
                ) : (
                  <Badge
                    appearance="filled"
                    color={STATUS_COLOR[p.status] ?? 'subtle'}
                    size="small"
                  >
                    {STATUS_LABEL[p.status] ?? p.status}
                  </Badge>
                )}
              </TableCell>
              <TableCell className={styles.actionCell}>
                <div className={styles.actionRow}>
                  <Button
                    appearance="primary"
                    size="small"
                    onClick={() => setDialogProject(p)}
                    title="Associer ou créer un groupe Microsoft 365"
                  >
                    Associer
                  </Button>
                  <a
                    className={styles.prismLink}
                    href={`https://prism.agence-chabanne.fr/?tab=Projet&projet=${encodeURIComponent(p.prism_id)}&subtab=Accueil`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<OpenRegular />}
                      title="Ouvrir dans PRISM"
                    />
                  </a>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {dialogProject && (
        <ProjectGroupDialog
          project={dialogProject}
          open
          onOpenChange={(o) => {
            if (!o) setDialogProject(null);
          }}
        />
      )}
    </>
  );
}
